import type {
  ShopperAiTagGroupId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { useLiveQuery } from "@tanstack/react-db";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import {
  buildGroupedTagListItems,
  tagToBadgeProps,
} from "../components/tag-list-utils";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

const tagSearcher = new Fuse<TagResponse>([], {
  keys: ["name"],
  threshold: 0.3,
});

export interface UseTagSelectorResult {
  tagListItems: RedoListItem<TagResponse>[];
  selectedTagIds: readonly ShopperAiTagId[];
  canEditTags: boolean;
  loading: boolean;

  handleTagToggled: (tag: TagResponse) => void;

  searchText: string;
  setSearchText: (text: string) => void;
}

export function useTagSelector({
  currentTagIds,
  onTagsChanged,
}: {
  currentTagIds: readonly ShopperAiTagId[];
  onTagsChanged: (newTagIds: ShopperAiTagId[]) => void;
}): UseTagSelectorResult {
  const collections = useGlobalSupportTicketCollections();
  const tagsResult = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [collections.tags],
  );
  const tagGroupsResult = useLiveQuery(
    (q) => q.from({ tagGroup: collections.tagGroups }),
    [collections.tagGroups],
  );
  const tags = tagsResult.data;
  const tagGroups = tagGroupsResult.data;
  const loading = tagsResult.isLoading || tagGroupsResult.isLoading;

  const [searchText, setSearchText] = useState("");
  const canEditTags = useHasPermission(SupportPermissions.MANAGE_TAG);

  const tagsArray = useMemo(() => [...tags], [tags]);
  const filteredTags = useSearch<TagResponse>(
    tagSearcher,
    tagsArray,
    searchText,
  );

  const tagGroupMap = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagGroupResponse>();
    for (const group of tagGroups) {
      map.set(group.id, group);
    }
    return map;
  }, [tagGroups]);

  const tagListItems: RedoListItem<TagResponse>[] = useMemo(() => {
    if (searchText) {
      return filteredTags.map((tag) => ({
        type: "badge" as const,
        id: tag.id,
        badge: tagToBadgeProps(tag),
        value: tag,
      }));
    }
    return buildGroupedTagListItems(filteredTags, tagGroups);
  }, [filteredTags, tagGroups, searchText]);

  const tagsByGroup = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagResponse[]>();
    for (const tag of tags) {
      const existing = map.get(tag.tagGroupId);
      if (existing) {
        existing.push(tag);
      } else {
        map.set(tag.tagGroupId, [tag]);
      }
    }
    return map;
  }, [tags]);

  const handleTagToggled = useHandler((tag: TagResponse) => {
    const currentSet = new Set(currentTagIds);
    if (currentSet.has(tag.id)) {
      currentSet.delete(tag.id);
    } else {
      const group = tagGroupMap.get(tag.tagGroupId);
      //if the group does not allow multiple tags, remove any existing tags from that group
      if (group && !group.allowMultipleTags) {
        const groupTags = tagsByGroup.get(tag.tagGroupId) ?? [];
        for (const groupTag of groupTags) {
          currentSet.delete(groupTag.id);
        }
      }
      currentSet.add(tag.id);
    }
    const newTagIds = [...currentSet];
    onTagsChanged(newTagIds);
  });

  return {
    tagListItems,
    selectedTagIds: currentTagIds,
    canEditTags,
    loading,
    handleTagToggled,
    searchText,
    setSearchText,
  };
}
